<script setup>
    import { getEnv } from '~/composables/helpers/getEnv';
    import { isArchivedSession } from '~~/shared/utilities/schedule';
    import { useScheduleControls } from '@/composables/useScheduleControls';
    import SessionsTags from '~/components/blocks/two-five/sessions/tags.vue';

    const { $bus, $clientPosthog } = useNuxtApp();
    const { loggedIn, user, fetch: refreshSession } = useUserSession();
    const { showSeatReservations } = useScheduleControls();
    const userApi = useUserApi();

    const props = defineProps({
        session: {
            type: Object,
            required: true,
        },
        layout: {
            type: String,
            required: false,
            default: 'wide',
        },
        show_sponsors: {
            type: Boolean,
            required: false,
            default: false,
        },
        show_tags: {
            type: Boolean,
            required: false,
            default: true,
        },
        hide_featured: {
            type: Boolean,
            required: false,
            default: false,
        },
        useA: {
            type: Boolean,
            required: false,
            default: false,
        },
        teal: {
            type: Boolean,
            required: false,
            default: false,
        },
        full: {
            type: Boolean,
            required: false,
            default: false,
        },
        disableLink: {
            type: Boolean,
            required: false,
            default: false,
        },
    });

    const cardHover = ref(false);
    const isFavorite = ref(null);
    const busy = ref(false);

    const emit = defineEmits(['load', 'load_speaker']);

    const hasPassed = computed(() => {
        if (!props.session.end_date_and_time) return false;
        const endTime = new Date(props.session.end_date_and_time);
        const currentTime = new Date();
        return currentTime > endTime;
    });

    const thumbailForSpeaker = (speaker) => {
        if (speaker.profile_image?.length && speaker.profile_image[0]?.url) {
            return speaker.profile_image[0];
        }
        return null;
    };

    const zIndex = (index) => {
        return props.session.speakers?.length - index;
    };

    const speakers = computed(() => {
        if (!props.session.speakers?.length) return [];
        return props.session.speakers.slice(0, 5);
    });

    // Archived (past-year) sessions don't show date/time/location — matches
    // compact-session-card / session header.
    const isArchive = computed(() => isArchivedSession(props.session));

    // Seat controls ride the same global release timestamp as every other Save
    // a Seat affordance (useScheduleControls → enableSeatReservationsAfter).
    // The button was pulled from this card in 478e0a39 on the read that the grid
    // is archive-only; it isn't — the live Sessions block passes showFullCard,
    // so the grid view needs the seat control the calendar view has. !isArchive
    // keeps it off past-year sessions, which is what that commit was after.
    const canSaveSeat = computed(() =>
        showSeatReservations.value
        && !isArchive.value
        && !props.session.is_external
        && props.session.session_reservations
    );

    // Date • time line joined with the shared bullet separator: nbsp + space
    // each side so two spaces render (HTML collapses plain double-spaces to
    // one) — matches detail-card / compact-session-card.
    const dateLine = computed(() => (props.session.display_date || []).join("  •  "));

    const handleSkillClick = async (level) => {
        if (!props.sessionsUrl) await navigateTo({ query: { level: level } });
        else if (props.sessionsUrl && props.sessionsUrl.includes('?')) await navigateTo({ path: props.sessionsUrl, query: { level: level } });
        else if (props.sessionsUrl) await navigateTo({ path: props.sessionsUrl, query: { level: level } });
    };

    const handleFavoriteClick = async () => {
        $clientPosthog?.capture('session_favorited', {
            session_id: props.session.id,
            session_title: props.session.title
        });
        if (!loggedIn.value) {
            await navigateTo({ path: '/login', query: { action: 'favorite' } });
            return;
        }
        try {
            busy.value = true;
            isFavorite.value = true;
            await userApi.favorite(props.session.unique_id);
            await refreshSession();
            $bus.emit('favorited', props.session);
        } catch (error) {
            window.alert('There was an error adding this session to your favorites. Please try again later.');
            isFavorite.value = null;
        } finally {
            busy.value = false;
        }
    };

    const session_link = computed(() => {
        if (props.session.is_external) {
            return getEnv('braindateUrl') + `?ext-id=${user.value.braindateHash}`;
        }
        const id = (props.session.unique_id || props.session.id || '').toLowerCase();
        return `/sessions/${id}`;
    });

    const is_favorited = computed(() => {
        // Check if user.favorites exists and contains the session unique_id
        const userHasFavorited = user.value?.favorites?.includes(props.session.unique_id);
        return isFavorite.value === null ? userHasFavorited : isFavorite.value;
    });

    const is_reserved = computed(() => {
        // sessionRegistration holds Bizzabo session ids, so it is compared
        // against the reconciled bizzabo_session_id — unique_id is a
        // Sessionboard friendly_id and never appears in that list.
        const bizzaboId = props.session.bizzabo_session_id;
        return !!bizzaboId && !!user.value?.sessionRegistration?.includes(bizzaboId);
    });

    const handleUnfavoriteClick = async () => {
        $clientPosthog?.capture('session_unfavorited', {
            session_id: props.session.id,
            session_title: props.session.title
        });
        if (!loggedIn.value) {
            await navigateTo({ path: '/login' });
            return;
        }
        try {
            busy.value = true;
            isFavorite.value = false;
            await userApi.unfavorite(props.session.unique_id);
            await refreshSession();
        } catch (error) {
            window.alert('There was an error adding this session to your favorites. Please try again later.');
            isFavorite.value = null;
        } finally {
            busy.value = false;
        }
    };

    const handleTitleClick = async (event) => {
        $clientPosthog?.capture('session_clicked', {
            session_id: props.session.id,
            session_title: props.session.title
        });
        if (props.disableLink) {
            event.preventDefault();
            emit('load', props.session);
            return false;
        }
    };

    const handleSpeakerClick = async (event, speaker) => {
        if (props.disableLink) {
            event.preventDefault();
            emit('load_speaker', speaker);
            return false;
        }
    };

    const handleReservationClick = async () => {
        $clientPosthog?.capture('session_reserve_clicked', {
            session_id: props.session.id,
            session_title: props.session.title
        });
        // .value — hasPassed is a computed, so the bare ref is always truthy and
        // this guard used to swallow every click.
        if (hasPassed.value) return;
        if (!loggedIn.value) {
            await navigateTo({ path: '/login', query: { action: 'reservations' } });
            return;
        }
        await navigateTo({ path: `/reservations/${props.session.id}` });
        return;
    };

    const handleUnreservationClick = async () => {
        $clientPosthog?.capture('session_unreserve_clicked', {
            session_id: props.session.id,
            session_title: props.session.title
        });
        if (hasPassed.value) return;
        if (!loggedIn.value) {
            await navigateTo({ path: '/login', query: { action: 'reservations' } });
            return;
        }
        await navigateTo({ path: `/leave/${props.session.id}` });
        return;
    };

    const buttonStylesOrange = {
        base: 'bg-[var(--orange)] border-1 border-[var(--orange)] text-white font-hubspot-serif font-normal text-[0.9375rem] rounded-[50px] hover:bg-[var(--magenta)] hover:border-[var(--magenta)] hover:text-white [&:hover_svg]:text-white',
        leadingIcon: 'mr-2 text-white',
    };

</script>
<template>
    <div class="session-card flex flex-col bg-white p-[var(--box-padding-lr-m)] lg:p-10 rounded-[var(--radius)] overflow-hidden snap-start transition-shadow duration-250 ease-in-out" :class="[teal ? 'teal' : '', cardHover ? 'hover' : '', busy ? 'busy-card' : '', layout]">
        <!-- session_reservations, not reservations_required: one field drives the
             whole card, matching the 2026 cards. (The first v-if also had a
             bitwise & where && was meant.) -->
        <div class="bugs flex items-center gap-6" v-if="session.session_reservations || session.featured || session.livestream">
            <span class="required-reservation" v-if="full && session.session_reservations && !session.is_full">Reservation Required</span>
            <span class="required-reservation full" v-if="full && session.session_reservations && session.is_full">Reservations Full</span>
            <span class="featured" v-if="!hide_featured && session.featured">Featured</span>
            <span class="on-demand" v-if="false && session.livestream">
                <UIcon name="local-on-demand" />
                On Demand
            </span>
        </div>
        <p class="time" v-if="full && session.start_date_and_time && !isArchive">{{ dateLine }}</p>
        <MessLink :href="session_link" :label="session.title" @mouseover="cardHover = true" @mouseleave="cardHover = false" @click="handleTitleClick">
            <h3 v-html="session.title"></h3>
            <p class="description" v-html="session.description" v-if="layout === 'wide'"></p>
            <p class="recommendation-reason" v-if="session.recommendationReason" :data-score="session.score">
                <UIcon name="local-assistant" class="breeze-icon" />
                <span v-html="session.recommendationReason"></span>
            </p>
        </MessLink>
        <template v-if="session.speakers?.length > 0">
            <div class="speakers flex flex-wrap items-center mb-0 gap-[1.1875rem] lg:mb-[1.3125rem] lg:gap-[1.3125rem]">
                <ul class="speaker-imgs flex flex-row items-center">
                    <template v-if="speakers" v-for="(speaker, index) in speakers" :key="speaker.id">
                        <li v-if="speaker.profile_image?.length && speaker.profile_image[0]?.url" :style="`--zIndex: ${zIndex(index)}`">
                            <MessLink :href="`/speakers/${speaker.slug || speaker.id}`" class="speaker-link" @click="handleSpeakerClick(speaker)" :external="disableLink">
                                <MessImage v-if="speaker.profile_image[0]" :image="thumbailForSpeaker(speaker)" lazy class="speaker-image" :alt="`${speaker.name} profile photo`" />
                            </MessLink>
                        </li>
                        <li v-else>
                            <img src="@/assets/img/speaker-default-small.jpg" alt="Generic person image" loading="lazy" />
                        </li>
                    </template>
                </ul>
                <ul class="speaker-names flex flex-wrap flex-row items-center mb-0 font-hubspot-serif text-[16px] font-normal leading-[140%] tracking-[0.16px]">
                    <li v-if="speakers" v-for="(speaker, index) in speakers" :key="speaker.id">
                        <MessLink :href="`/speakers/${speaker.slug || speaker.id}`" class="speaker-link">
                            {{ speaker.name }}
                        </MessLink>
                        <span class="comma" v-if="index < speakers.length - 1" inert>,&nbsp;</span>
                    </li>
                </ul>
            </div>
        </template>
        <div class="watch flex gap-4 mb-[2.5625rem]" v-if="session.livestream_link">
            <UButton v-if="session.livestream_link" :to="session_link" icon="local-play" :ui="buttonStylesOrange" @click="$clientPosthog?.capture('session_watch_on_demand_clicked', { session_id: session.id, session_title: session.title })">Watch On Demand</UButton>
        </div>
        <p class="skill-level hidden" v-if="false && session.skill_level" @click.prevent="handleSkillClick(session.skill_level)" v-html="session.skill_level"></p>
        <SessionsTags v-if="show_tags" :session="session" />
        <div class="sponsors mt-4" v-if="show_sponsors && (session.sponsors?.length || session.also_sponsors?.length)">
            <FragmentsSponsorsSponsoredBy :sponsors="session.sponsors" />
            <FragmentsSponsorsAlsoSponsoredBy :sponsors="session.also_sponsors" />
        </div>
        <!-- Seat actions. canSaveSeat carries the global release gate, so the row
             itself hangs off it too — otherwise the padding shows as a phantom
             gap under every card before seat saving opens. Favorites are retired
             for 2026 and are not restored here. -->
        <div class="actions flex flex-col gap-[1.22875rem] mt-auto pt-[1.3125rem] lg:gap-[2.1875rem] lg:flex-row" v-if="full && session.unique_id && canSaveSeat">
            <ClientOnly>
                <UButton
                    v-if="!hasPassed && !is_reserved && !session.is_full"
                    :variant="teal ? 'sage' : 'transparent'"
                    icon="local-seat-empty"
                    aria-label="Save a Seat"
                    @click="handleReservationClick">
                    Save a Seat
                </UButton>
                <UButton
                    v-if="is_reserved"
                    :variant="teal ? 'sage' : 'transparent'"
                    icon="local-seat-filled"
                    :class="teal ? 'text-orange-for-teal' : 'text-orange'"
                    aria-label="Seat Saved"
                    @click="handleUnreservationClick">
                    Seat Saved
                </UButton>
            </ClientOnly>
        </div>
    </div>
</template>

<style>
    .session-card.hover {
        box-shadow: inset 0 0 0 1px var(--pink);
    }

    .session-card .bugs .featured {
        margin-bottom: 1rem;
    }

    .session-card .required-reservation {
        width: fit-content;
        display: flex;
        color: white;
        font-size: 0.75rem;
        font-weight: 500;
        line-height: 1.1875rem;
        letter-spacing: 0.0375rem;
        text-transform: uppercase;
        padding: 0.375rem 1rem;
        background-color: var(--orange);
        border-radius: var(--full-radius);
        margin-bottom: 0.9375rem;
    }

    .session-card .required-reservation.full {
        background-color: var(--black);
    }

    .session-card .on-demand {
        width: fit-content;
        display: flex;
        align-items: center;
        color: var(--magenta);
        font-size: 0.75rem;
        font-style: normal;
        font-weight: 500;
        line-height: 1.1875rem;
        padding: 0.375rem 1rem;
        background-color: transparent;
        border-radius: var(--full-radius);
        border: 1px solid var(--magenta);
        margin-bottom: 0.9375rem;
        gap: 0.5rem;
    }

    .session-card.teal.hover {
        box-shadow: inset 0 0 0 1px var(--teal-ring);
    }

    .session-card.teal .required-reservation {
        background-color: var(--orange);
    }

    .session-card.teal .required-reservation.full {
        background-color: var(--magenta);
    }

    .session-card.teal .on-demand {
        color: var(--magenta);
        border-color: var(--magenta);
    }

    .session-card .featured {
        display: block;
        text-transform: uppercase;
        font-size: 0.6875rem;
        font-weight: 500;
        color: var(--orange);
        line-height: 1.25rem;
        letter-spacing: 0.040625rem;
        margin-bottom: 0.5625rem;
    }

    @media (min-width: 1024px) {
        .session-card .featured {
            font-size: 0.8125rem;
        }
    }

    .session-card .time {
        font-size: 0.75rem;
        font-weight: 500;
        line-height: 1.625rem;
        margin-bottom: 0.5rem;
        letter-spacing: 0rem;
    }

    @media (min-width: 1024px) {
        .session-card .time {
            font-size: 0.9375rem;
        }
    }

    .session-card a.mess-link:hover {
        color: var(--orange);
    }

    .session-card h3 {
        font-family: var(--font-hubspot-serif);
        font-size: 1.6875rem;
        font-weight: 400;
        line-height: 1.3;
        letter-spacing: 0;
        margin-bottom: 0.8125rem;
    }

    .session-card h3 a:hover {
        color: var(--orange);
    }

    @media (min-width: 1024px) {
        .session-card h3 {
            font-size: 2.25rem;
            line-height: 1.3;
            margin-bottom: 1.125rem;
        }
    }

    .session-card .description {
        font-family: var(--font-hubspot-sans);
        font-size: 1rem;
        font-weight: 300;
        line-height: 26px;
        letter-spacing: 0.32px;
        margin-bottom: 1.4375rem;
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .session-card .description.hover {
        color: var(--orange);
    }

    @media (min-width: 1024px) {
        .session-card .description {
            line-height: 1.5625rem;
            margin-bottom: 1.875rem;
        }
    }

    .session-card.teal .speaker-names a {
        /* no default override needed */
    }

    @media (hover: hover) {
        .session-card.teal .speaker-names a:hover {
            color: var(--orange);
        }
    }

    .session-card .recommendation-reason {
        color: var(--magenta);
        font-size: 0.8125rem;
        font-style: normal;
        font-weight: 300;
        line-height: 1.1875rem;
        letter-spacing: 0.01625rem;
        margin-bottom: 1.75rem;
        padding: 1.25rem;
        background: linear-gradient(90deg, #fff0f4 0%, #ffe9e0 100%);
        border-radius: var(--radius);
    }

    .session-card .recommendation-reason .breeze-icon {
        width: 0.875rem;
        height: 0.875rem;
        margin-right: 0.5rem;
        vertical-align: middle;
    }

    .session-card.wide .speakers {
        margin-bottom: 1.1875rem;
    }

    @media (min-width: 1024px) {
        .session-card.wide .speakers {
            margin-bottom: 2.5625rem;
        }
    }

    .session-card.teal .speaker-imgs li {
        border-color: var(--black);
    }

    .session-card .skill-level {
        width: fit-content;
        font-family: var(--font-hubspot-sans);
        font-size: 12px;
        font-weight: 400;
        line-height: 26px;
        letter-spacing: 0.24px;
        text-transform: none;
        margin-bottom: 1.125rem;
        padding: 0 10px;
        border-radius: 9999px;
        border: 1px solid var(--magenta);
        cursor: pointer;
    }

    @media (hover: hover) {
        .session-card .skill-level:hover {
            background-color: var(--magenta);
            color: white;
        }
    }

    .session-card.teal .skill-level {
        border-color: white;
    }

    @media (hover: hover) {
        .session-card.teal .skill-level:hover {
            background-color: var(--orange);
            border-color: var(--orange);
            color: white;
        }
    }

    .session-card .session-tags-list.tags {
        margin-bottom: 1.375rem;
    }

    .session-card.teal .actions button:hover {
        color: white;
    }

    .session-card .speaker-imgs li {
        width: 4.5rem;
        height: 4.5rem;
        border-radius: 50%;
        z-index: var(--zIndex);
        border: 2px solid white;
        overflow: hidden;
        aspect-ratio: 1 / 1;
    }

    .session-card .speaker-imgs li:not(:first-child) {
        margin-left: -0.375rem;
    }

    @media (hover: hover) {
        .session-card .speaker-imgs li a:hover img {
            transform: scale(var(--scale));
        }
    }

    .session-card .speaker-imgs li img {
        width: 4.5rem;
        height: 4.5rem;
        border-radius: 50%;
        object-position: center;
        object-fit: cover;
        transition: transform 250ms ease-in-out;
        aspect-ratio: 1 / 1;
    }
</style>
